import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { api } from "../../utils/axios";
import "./samlSetup.css";

const SamlSetupPage: React.FC = () => {
  const clientId = useAppSelector((s) => s.auth.clientID);
  const [metadataUrl, setMetadataUrl] = useState("");
  const ssoUrl = `${
    process.env.REACT_APP_API_URL || "http://localhost:8000"
  }/authentication/sso/acs/${clientId}`;
  const audienceUri = `${
    process.env.REACT_APP_API_URL || "http://localhost:8000"
  }/sp/${clientId}`;
  const [loading, setLoading] = useState(false);

  const [manualValues, setManualValues] = useState({
    entity_id: "",
    sso_url: "",
    sign_out_url: "",
    x509cert: "",
  });

  const [downloadedPublicKey, setDownloadedPublicKey] = useState(false);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch {
      toast.error("Failed to copy.");
    }
  };

  const handleDownloadPublicKey = async () => {
    setLoading(true);
    if (!clientId) {
      toast.error("Missing client ID");
      return;
    }
    try {
      const res = await api.get(`/authentication/sso/public/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "public-key.pem");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Public key downloaded!");
      setDownloadedPublicKey(true);
    } catch {
      toast.error("Failed to download public key.");
    } finally {
      setLoading(false);
    }
  };

  const isSubmitDisabled = useMemo(() => {
    if (loading) return true;

    const metadataFilled = metadataUrl.trim().length > 0;
    const allManualFilled =
      manualValues.entity_id.trim().length > 0 &&
      manualValues.sso_url.trim().length > 0 &&
      manualValues.x509cert.trim().length > 0;
    const orManualFilled =
      manualValues.entity_id.trim().length > 0 ||
      manualValues.sso_url.trim().length > 0 ||
      manualValues.x509cert.trim().length > 0;

    // Disable if neither option is filled
    if (!metadataFilled && !orManualFilled) return true;

    // Disable if both options are filled (invalid case)
    if (metadataFilled && orManualFilled) return true;

    // Disable if metadata is not filled and all manual not filled
    if (!metadataFilled && !allManualFilled) return true;

    return false;
  }, [loading, metadataUrl, manualValues]);

  const handleSubmit = async () => {
    setLoading(true);
    if (!clientId) return toast.error("Missing client ID");
    if (metadataUrl.trim() && manualValues.entity_id) {
      toast.error("Please provide either metadata URL or manual fields");
      setLoading(false);
      return;
    }

    try {
      if (metadataUrl.trim()) {
        await api.post("/authentication/sso/metadata", {
          metadata_url: metadataUrl,
          client_id: clientId,
        });
        toast.success("Metadata processed successfully");
      } else if (
        manualValues.entity_id &&
        manualValues.sso_url &&
        manualValues.x509cert
      ) {
        try {
          await api.post("/authentication/sso/metadata/by-value", {
            client_id: clientId,
            ...manualValues,
          });
          toast.success("SAML settings saved successfully");
        } catch (error) {
          toast.error(`Failed to save SAML settings. Please check the URL`);
        }
        
      } else {
        toast.error("Please provide either metadata URL or all fields");
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error submitting data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="saml-setup-container">
      <ToastContainer position="top-right" />
      <h2 className="setup-title">SAML Setup for Your Organization</h2>

      <div className="setup-step">
        <h3>Step 1: Add our app to your Identity Provider</h3>
        <p>Please copy and configure the following in your IdP:</p>

        <div className="setup-field">
          <label>SSO URL</label>
          <div className="copy-box">
            <span>{ssoUrl}</span>
            <button onClick={() => handleCopy(ssoUrl)}>Copy</button>
          </div>
        </div>

        <div className="setup-field">
          <label>Audience URI (SP Entity ID)</label>
          <div className="copy-box">
            <span>{audienceUri}</span>
            <button onClick={() => handleCopy(audienceUri)}>Copy</button>
          </div>
        </div>

        <div className="setup-field">
          <h3>(Optional) If you want encryption</h3>
          <label>Download Public Key (PEM)</label>
          <button
            className="download-button"
            onClick={handleDownloadPublicKey}
            disabled={loading || downloadedPublicKey}
          >
            Download PEM
          </button>
          {downloadedPublicKey && (
            <span className="download-success">✅ Public key downloaded!</span>
          )}
        </div>

        <p className="setup-field">
          <strong>NameID Format</strong>: Email Address
        </p>
      </div>

      <div className="setup-step">
        <h3>Step 2: Provide Your IdP Configuration</h3>

        <label htmlFor="metadata-url">Option 1: Metadata URL</label>
        <input
          id="metadata-url"
          className="metadata-input"
          type="text"
          placeholder="https://your-idp.com/metadata"
          value={metadataUrl}
          onChange={(e) => setMetadataUrl(e.target.value)}
        />

        <div className="setup-divider">OR</div>

        <label>Option 2: Manual Entry</label>

        <input
          className="metadata-input"
          placeholder="SAML Issuer (Entity ID)"
          value={manualValues.entity_id}
          onChange={(e) =>
            setManualValues({ ...manualValues, entity_id: e.target.value })
          }
        />
        <input
          className="metadata-input"
          placeholder="SSO Login URL"
          value={manualValues.sso_url}
          onChange={(e) =>
            setManualValues({ ...manualValues, sso_url: e.target.value })
          }
        />
        <input
          className="metadata-input"
          placeholder="SSO Logout URL (optional)"
          value={manualValues.sign_out_url}
          onChange={(e) =>
            setManualValues({ ...manualValues, sign_out_url: e.target.value })
          }
        />
        <textarea
          className="metadata-input"
          placeholder="X.509 Certificate"
          rows={5}
          value={manualValues.x509cert}
          onChange={(e) =>
            setManualValues({ ...manualValues, x509cert: e.target.value })
          }
        />

        <button
          className="submit-button"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          Submit Configuration
        </button>
      </div>
    </div>
  );
};

export default SamlSetupPage;
